import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { Medication } from '@/types/medication';

/**
 * MedicationScheduleProps interface
 */
export interface MedicationScheduleProps {
  /** Optional demo mode flag */
  isDemo?: boolean;
  /** Optional initial medications data */
  initialMedications?: Medication[];
}

/**
 * Fixed implementation of MedicationScheduleContainer that properly handles medication events
 * with improved accessibility and diagnostic logging
 */
export const FixedMedicationScheduleContainer: React.FC<MedicationScheduleProps> = ({ 
  isDemo = false,
  initialMedications = []
}) => {
  const { toast } = useToast();
  const [medications, setMedications] = useState<any[]>(initialMedications || []);
  const [isLoading, setIsLoading] = useState(false);
  
  // Setup medication added event listener with proper logging
  useEffect(() => {
    console.log("[DEBUG] FixedMedicationScheduleContainer: registering medication-added listener");
    
    // Enhanced event handler with detailed diagnostics
    const handleMedicationAdded = (event: CustomEvent<any>) => {

      
      // Safely extract medication data with robust error handling
      if (!event || !event.detail) {
        console.error("[ERROR] Invalid event or missing detail in medication-added event");
        return;
      }
      
      // Support both nesting formats (direct or inside 'medication' property)
      const medicationData = event.detail.medication || event.detail;

      
      // Verify required fields exist
      if (!medicationData.name) {
        console.error("[ERROR] Medication name is required");
        toast({
          title: "Error Adding Medication",
          description: "Medication name is required", 
          variant: "destructive"
        });
        return;
      }
      
      // Update state with new medication
      setMedications(prev => [...prev, medicationData]);
      
      // Show success message
      toast({
        title: "Medication Added",
        description: `${medicationData.name} has been added to your schedule.`
      });
      
      console.log("[SUCCESS] Medication added to schedule:", medicationData);
    };
    
    // Add event listener for medication added events
    window.addEventListener('meduler:medication-added', handleMedicationAdded as EventListener);
    
    return () => {
      window.removeEventListener('meduler:medication-added', handleMedicationAdded as EventListener);
    };
  }, [toast]);
  
  return (
    <div className="w-full max-w-6xl mx-auto rounded-lg overflow-hidden bg-card">
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Your Medication Schedule</h2>
        
        {isLoading ? (
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-6 py-1">
              <div className="h-2 bg-slate-200 rounded"></div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-2 bg-slate-200 rounded col-span-2"></div>
                  <div className="h-2 bg-slate-200 rounded col-span-1"></div>
                </div>
                <div className="h-2 bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
            {medications.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {medications.map((medication, index) => (
                  <li key={medication.id || index} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{medication.name}</p>
                        <p className="text-sm text-gray-500">{medication.dosage}</p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {medication.timing || medication.time || 'Not specified'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <p>No medications added yet</p>
                <p className="text-sm mt-2">Add medication using the "Add Medication" button above</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FixedMedicationScheduleContainer;
